import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, CardContent, Typography, Box, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Chip,
  Grid, makeStyles, CircularProgress, Button, TextField,
  IconButton, Tooltip, LinearProgress
} from '@material-ui/core';
import VisibilityIcon from '@material-ui/icons/Visibility';
import TimerIcon from '@material-ui/icons/Timer';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import SmartphoneIcon from '@material-ui/icons/Smartphone';
import DateRangeIcon from '@material-ui/icons/DateRange';
import RefreshIcon from '@material-ui/icons/Refresh';
import PeopleOutlineIcon from '@material-ui/icons/PeopleOutline';
import AssessmentIcon from '@material-ui/icons/Assessment';
import EventNoteIcon from '@material-ui/icons/EventNote';
import { getPageAnalytics } from '../services/pageViewService';

const useStyles = makeStyles((theme) => ({
  card: {
    borderRadius: 20,
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,
    marginTop: theme.spacing(4),
    boxShadow: '0 4px 24px rgba(0,0,0,0.2)',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 16,
    marginBottom: theme.spacing(3),
  },
  title: {
    fontWeight: 700,
    fontSize: '1.25rem',
    color: theme.palette.text.primary,
    fontFamily: '"Plus Jakarta Sans", "Inter", sans-serif',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  iconBadge: {
    width: 40,
    height: 40,
    borderRadius: 12,
    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 6px 16px rgba(16,185,129,0.3)',
  },
  rangeBar: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
    background: theme.palette.type === 'dark' ? 'rgba(15,23,42,0.6)' : 'rgba(241,245,249,0.8)',
    padding: '6px 10px',
    borderRadius: 14,
    border: `1px solid ${theme.palette.divider}`,
  },
  rangeBtn: {
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.78rem',
    borderRadius: 8,
    padding: '4px 12px',
    minWidth: 0,
    color: theme.palette.text.secondary,
    '&.active': {
      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
      color: '#fff',
      boxShadow: '0 4px 12px rgba(16,185,129,0.35)',
    },
  },
  customRangeRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginTop: theme.spacing(1.5),
    padding: theme.spacing(1.5, 2),
    borderRadius: 12,
    background: theme.palette.type === 'dark' ? 'rgba(30,41,59,0.5)' : '#f8fafc',
    border: `1px solid ${theme.palette.divider}`,
  },
  metricBox: {
    padding: theme.spacing(2.2),
    borderRadius: 16,
    background: theme.palette.type === 'dark' ? 'rgba(30,41,59,0.5)' : 'rgba(248,250,252,0.8)',
    border: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    height: '100%',
    transition: 'all 0.25s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      borderColor: 'rgba(16,185,129,0.4)',
      boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
    },
  },
  chartContainer: {
    background: theme.palette.type === 'dark' ? 'rgba(15,23,42,0.5)' : 'rgba(248,250,252,0.6)',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 16,
    padding: theme.spacing(2.5),
    marginBottom: theme.spacing(3),
  },
  chartHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: theme.spacing(2),
  },
  barsWrapper: {
    display: 'flex',
    alignItems: 'flex-end',
    height: 180,
    gap: 8,
    padding: '16px 4px 8px',
    overflowX: 'auto',
  },
  barCol: {
    flex: 1,
    minWidth: 32,
    maxWidth: 52,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    height: '100%',
    justifyContent: 'flex-end',
    position: 'relative',
  },
  barFill: {
    width: '100%',
    borderRadius: '6px 6px 0 0',
    background: 'linear-gradient(180deg, #10b981 0%, #059669 100%)',
    transition: 'height 0.4s cubic-bezier(0.4,0,0.2,1), filter 0.2s ease',
    cursor: 'pointer',
    position: 'relative',
    '&:hover': {
      filter: 'brightness(1.25)',
      boxShadow: '0 0 16px rgba(16,185,129,0.6)',
    },
  },
  barFillToday: {
    background: 'linear-gradient(180deg, #38bdf8 0%, #0284c7 100%) !important',
    boxShadow: '0 0 12px rgba(14,165,233,0.5)',
  },
  barCount: {
    fontSize: '0.72rem',
    fontWeight: 700,
    color: theme.palette.text.primary,
    marginBottom: 4,
    whiteSpace: 'nowrap',
  },
  barLabel: {
    fontSize: '0.68rem',
    color: theme.palette.text.secondary,
    marginTop: 6,
    whiteSpace: 'nowrap',
    fontWeight: 500,
  },
  barDayName: {
    fontSize: '0.62rem',
    color: theme.palette.text.secondary,
    opacity: 0.8,
  },
  table: {
    minWidth: 650,
  },
  tableHeaderCell: {
    fontWeight: 700,
    fontSize: '0.78rem',
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  tableRow: {
    '&:hover': {
      background: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
    },
  },
  rankBadge: {
    width: 26,
    height: 26,
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700,
    fontSize: '0.78rem',
  },
  viewToggleBtn: {
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.8rem',
    borderRadius: 8,
    padding: '5px 12px',
    color: theme.palette.text.secondary,
    '&.active': {
      background: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)',
      color: theme.palette.text.primary,
      fontWeight: 700,
    },
  },
}));

function formatTime(seconds) {
  if (!seconds || seconds === 0) return '0s';
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hrs > 0) return `${hrs}h ${mins}m`;
  if (mins > 0) return `${mins}m ${secs}s`;
  return `${secs}s`;
}

export default function PageAnalyticsSection() {
  const classes = useStyles();
  const [range, setRange] = useState('7days');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [showCustom, setShowCustom] = useState(false);
  const [activeTab, setActiveTab] = useState('screens'); // 'screens' | 'daily'
  const [loading, setLoading] = useState(true);

  const [data, setData] = useState({
    pages: [],
    totalViews: 0,
    totalSeconds: 0,
    avgSecondsOverall: 0,
    dailyTrend: [],
    dailyAverageViews: 0,
    todayViews: 0,
    yesterdayViews: 0,
    uniqueUsersCount: 0,
    peakDay: { label: 'None', views: 0 },
    startDateStr: '',
    endDateStr: '',
    daysCount: 7,
  });

  const fetchData = useCallback(async (opts = {}) => {
    setLoading(true);
    const targetRange = opts.range || range;
    const sDate = opts.startDate !== undefined ? opts.startDate : customStart;
    const eDate = opts.endDate !== undefined ? opts.endDate : customEnd;

    const result = await getPageAnalytics({
      range: targetRange,
      startDate: sDate,
      endDate: eDate,
    });
    setData(result);
    setLoading(false);
  }, [range, customStart, customEnd]);

  useEffect(() => {
    fetchData({ range: '7days' });
  }, [fetchData]);

  const handleRangeChange = (newRange) => {
    setRange(newRange);
    if (newRange === 'custom') {
      setShowCustom(true);
    } else {
      setShowCustom(false);
      fetchData({ range: newRange });
    }
  };

  const handleApplyCustom = () => {
    if (customStart && customEnd) {
      fetchData({ range: 'custom', startDate: customStart, endDate: customEnd });
    }
  };

  const maxDayViews = Math.max(1, ...(data.dailyTrend || []).map((d) => d.views || 0));
  const topPage = data.pages.length > 0 ? data.pages[0].pageName : 'N/A';

  // Calculate day-over-day growth for today vs yesterday
  const viewDelta = data.todayViews - data.yesterdayViews;
  const isPositiveDelta = viewDelta >= 0;

  return (
    <Card className={classes.card}>
      <CardContent style={{ padding: 24 }}>
        {/* Header with Title and Range Picker */}
        <div className={classes.header}>
          <Typography className={classes.title}>
            <div className={classes.iconBadge}>
              <VisibilityIcon fontSize="small" />
            </div>
            Screen & Page View Analytics
          </Typography>

          <Box display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 8 }}>
            <div className={classes.rangeBar}>
              <Button
                size="small"
                className={`${classes.rangeBtn} ${range === 'today' ? 'active' : ''}`}
                onClick={() => handleRangeChange('today')}
              >
                Today
              </Button>
              <Button
                size="small"
                className={`${classes.rangeBtn} ${range === 'yesterday' ? 'active' : ''}`}
                onClick={() => handleRangeChange('yesterday')}
              >
                Yesterday
              </Button>
              <Button
                size="small"
                className={`${classes.rangeBtn} ${range === '7days' ? 'active' : ''}`}
                onClick={() => handleRangeChange('7days')}
              >
                7 Days
              </Button>
              <Button
                size="small"
                className={`${classes.rangeBtn} ${range === '14days' ? 'active' : ''}`}
                onClick={() => handleRangeChange('14days')}
              >
                14 Days
              </Button>
              <Button
                size="small"
                className={`${classes.rangeBtn} ${range === '30days' ? 'active' : ''}`}
                onClick={() => handleRangeChange('30days')}
              >
                30 Days
              </Button>
              <Button
                size="small"
                className={`${classes.rangeBtn} ${range === 'thisMonth' ? 'active' : ''}`}
                onClick={() => handleRangeChange('thisMonth')}
              >
                This Month
              </Button>
              <Button
                size="small"
                className={`${classes.rangeBtn} ${range === 'all' ? 'active' : ''}`}
                onClick={() => handleRangeChange('all')}
              >
                All Time
              </Button>
              <Button
                size="small"
                className={`${classes.rangeBtn} ${range === 'custom' ? 'active' : ''}`}
                onClick={() => handleRangeChange('custom')}
                startIcon={<DateRangeIcon fontSize="small" />}
              >
                Custom
              </Button>
            </div>

            <Tooltip title="Refresh Analytics">
              <IconButton size="small" onClick={() => fetchData({ range })}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </div>

        {/* Custom Range Picker Drawer / Inputs */}
        {showCustom && (
          <div className={classes.customRangeRow}>
            <DateRangeIcon style={{ color: '#10b981' }} />
            <Typography variant="body2" style={{ fontWeight: 600, marginRight: 8 }}>
              Select Custom Date Range:
            </Typography>
            <TextField
              type="date"
              size="small"
              variant="outlined"
              label="Start Date"
              value={customStart}
              onChange={(e) => setCustomStart(e.target.value)}
              InputLabelProps={{ shrink: true }}
              style={{ width: 150 }}
            />
            <TextField
              type="date"
              size="small"
              variant="outlined"
              label="End Date"
              value={customEnd}
              onChange={(e) => setCustomEnd(e.target.value)}
              InputLabelProps={{ shrink: true }}
              style={{ width: 150 }}
            />
            <Button
              size="small"
              variant="contained"
              style={{ background: '#10b981', color: '#fff', textTransform: 'none', height: 40 }}
              onClick={handleApplyCustom}
              disabled={!customStart || !customEnd}
            >
              Apply Range
            </Button>
          </div>
        )}

        {/* Range Label Badge */}
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2.5}>
          <Typography variant="body2" color="textSecondary" style={{ fontWeight: 500 }}>
            Active Range: <strong style={{ color: '#10b981' }}>{data.startDateStr}</strong> to <strong style={{ color: '#10b981' }}>{data.endDateStr}</strong> ({data.daysCount} {data.daysCount === 1 ? 'day' : 'days'})
          </Typography>

          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
            <Button
              size="small"
              className={`${classes.viewToggleBtn} ${activeTab === 'screens' ? 'active' : ''}`}
              startIcon={<AssessmentIcon fontSize="small" />}
              onClick={() => setActiveTab('screens')}
            >
              Screens Breakdown
            </Button>
            <Button
              size="small"
              className={`${classes.viewToggleBtn} ${activeTab === 'daily' ? 'active' : ''}`}
              startIcon={<EventNoteIcon fontSize="small" />}
              onClick={() => setActiveTab('daily')}
            >
              Daily Log
            </Button>
          </Box>
        </Box>

        {loading ? (
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8}>
            <CircularProgress size={36} style={{ color: '#10b981' }} />
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 12 }}>
              Aggregating page views and screen telemetry...
            </Typography>
          </Box>
        ) : (
          <>
            {/* 4 Metric Summary Cards */}
            <Grid container spacing={2} style={{ marginBottom: 24 }}>
              {/* Metric 1: Daily Page Views (Today) */}
              <Grid item xs={12} sm={6} md={3}>
                <div className={classes.metricBox}>
                  <VisibilityIcon style={{ color: '#10b981', fontSize: 34 }} />
                  <div>
                    <Typography variant="body2" color="textSecondary" style={{ fontWeight: 600 }}>
                      Daily Page Views (Today)
                    </Typography>
                    <Typography variant="h5" style={{ fontWeight: 800 }}>
                      {data.todayViews.toLocaleString()}
                    </Typography>
                    <Typography variant="caption" style={{ color: isPositiveDelta ? '#10b981' : '#f59e0b', fontWeight: 600 }}>
                      {isPositiveDelta ? '↑' : '↓'} {data.yesterdayViews} yesterday
                    </Typography>
                  </div>
                </div>
              </Grid>

              {/* Metric 2: Total Views in Range */}
              <Grid item xs={12} sm={6} md={3}>
                <div className={classes.metricBox}>
                  <TrendingUpIcon style={{ color: '#0ea5e9', fontSize: 34 }} />
                  <div>
                    <Typography variant="body2" color="textSecondary" style={{ fontWeight: 600 }}>
                      Total Views in Range
                    </Typography>
                    <Typography variant="h5" style={{ fontWeight: 800 }}>
                      {data.totalViews.toLocaleString()}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Daily Avg: <strong>{data.dailyAverageViews.toLocaleString()}</strong> / day
                    </Typography>
                  </div>
                </div>
              </Grid>

              {/* Metric 3: Total Screen Time */}
              <Grid item xs={12} sm={6} md={3}>
                <div className={classes.metricBox}>
                  <TimerIcon style={{ color: '#8b5cf6', fontSize: 34 }} />
                  <div>
                    <Typography variant="body2" color="textSecondary" style={{ fontWeight: 600 }}>
                      Total Screen Time
                    </Typography>
                    <Typography variant="h5" style={{ fontWeight: 800 }}>
                      {formatTime(data.totalSeconds)}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Avg. <strong>{formatTime(data.avgSecondsOverall)}</strong> / view
                    </Typography>
                  </div>
                </div>
              </Grid>

              {/* Metric 4: Active Viewers / Peak Day */}
              <Grid item xs={12} sm={6} md={3}>
                <div className={classes.metricBox}>
                  <PeopleOutlineIcon style={{ color: '#f59e0b', fontSize: 34 }} />
                  <div>
                    <Typography variant="body2" color="textSecondary" style={{ fontWeight: 600 }}>
                      Active Viewers in Range
                    </Typography>
                    <Typography variant="h5" style={{ fontWeight: 800 }}>
                      {data.uniqueUsersCount > 0 ? `${data.uniqueUsersCount} users` : 'Active'}
                    </Typography>
                    <Typography variant="caption" color="textSecondary" noWrap display="block">
                      Peak: <strong>{data.peakDay?.label || 'None'}</strong> ({data.peakDay?.views || 0})
                    </Typography>
                  </div>
                </div>
              </Grid>
            </Grid>

            {/* Daily Page View Count Chart */}
            <div className={classes.chartContainer}>
              <div className={classes.chartHeader}>
                <div>
                  <Typography variant="subtitle1" style={{ fontWeight: 800, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <TrendingUpIcon style={{ color: '#10b981', fontSize: 20 }} />
                    Daily Page View Count
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Showing daily page views recorded across {data.daysCount} days
                  </Typography>
                </div>

                <Box display="flex" alignItems="center" style={{ gap: 12 }}>
                  <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                    <div style={{ width: 12, height: 12, borderRadius: 3, background: 'linear-gradient(180deg, #10b981 0%, #059669 100%)' }} />
                    <Typography variant="caption" color="textSecondary">Daily Views</Typography>
                  </Box>
                  <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                    <div style={{ width: 12, height: 12, borderRadius: 3, background: 'linear-gradient(180deg, #38bdf8 0%, #0284c7 100%)' }} />
                    <Typography variant="caption" color="textSecondary">Today</Typography>
                  </Box>
                </Box>
              </div>

              {data.dailyTrend.length === 0 ? (
                <Box p={4} textAlign="center" color="text.secondary">
                  <Typography variant="body2">No daily page views found in this date range.</Typography>
                </Box>
              ) : (
                <div className={classes.barsWrapper}>
                  {data.dailyTrend.map((day) => {
                    const heightPercent = Math.max(6, Math.round((day.views / maxDayViews) * 100));
                    const isToday = day.dateKey === `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}-${String(new Date().getDate()).padStart(2, '0')}`;

                    return (
                      <Tooltip
                        key={day.dateKey}
                        title={
                          <div>
                            <strong>{day.label} ({day.dayName})</strong>
                            <div>Page Views: <strong>{day.views}</strong></div>
                            {day.uniqueUsersCount > 0 && <div>Active Users: <strong>{day.uniqueUsersCount}</strong></div>}
                            <div>Total Time: <strong>{formatTime(day.totalSeconds)}</strong></div>
                            {day.topPageName !== 'None' && <div>Top Screen: <strong>{day.topPageName}</strong></div>}
                          </div>
                        }
                        arrow
                      >
                        <div className={classes.barCol}>
                          <span className={classes.barCount}>
                            {day.views > 0 ? day.views : ''}
                          </span>
                          <div
                            className={`${classes.barFill} ${isToday ? classes.barFillToday : ''}`}
                            style={{ height: `${heightPercent}%` }}
                          />
                          <span className={classes.barLabel}>
                            {day.label}
                          </span>
                          <span className={classes.barDayName}>
                            {day.dayName}
                          </span>
                        </div>
                      </Tooltip>
                    );
                  })}
                </div>
              )}
            </div>

            {/* TAB 1: Screens Breakdown */}
            {activeTab === 'screens' && (
              <>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                  <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                    Screen & Route Breakdown ({data.pages.length} screens)
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Most visited screen: <strong style={{ color: '#10b981' }}>{topPage}</strong>
                  </Typography>
                </Box>

                {data.pages.length === 0 ? (
                  <Box p={3} textAlign="center" color="text.secondary">
                    <Typography variant="body2">No screen telemetry recorded in this date range.</Typography>
                  </Box>
                ) : (
                  <TableContainer>
                    <Table className={classes.table} size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell className={classes.tableHeaderCell} width="6%">#</TableCell>
                          <TableCell className={classes.tableHeaderCell} width="28%">Screen / Feature</TableCell>
                          <TableCell className={classes.tableHeaderCell} width="20%">Route</TableCell>
                          <TableCell align="right" className={classes.tableHeaderCell} width="16%">Views in Range</TableCell>
                          <TableCell align="center" className={classes.tableHeaderCell} width="14%">% Share</TableCell>
                          <TableCell align="right" className={classes.tableHeaderCell} width="16%">Total Time Spent</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {data.pages.map((p, index) => (
                          <TableRow key={p.id || index} className={classes.tableRow}>
                            <TableCell>
                              <div
                                className={classes.rankBadge}
                                style={{
                                  background: index === 0 ? 'rgba(245,158,11,0.2)' : 'rgba(148,163,184,0.1)',
                                  color: index === 0 ? '#d97706' : 'inherit',
                                }}
                              >
                                {index + 1}
                              </div>
                            </TableCell>
                            <TableCell style={{ fontWeight: 600 }}>
                              <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                                <SmartphoneIcon fontSize="small" style={{ color: '#10b981', opacity: 0.8 }} />
                                {p.pageName}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip size="small" label={p.route || '/'} variant="outlined" style={{ fontSize: '0.75rem' }} />
                            </TableCell>
                            <TableCell align="right" style={{ fontWeight: 700, color: '#10b981' }}>
                              {p.totalViews.toLocaleString()}
                            </TableCell>
                            <TableCell align="center">
                              <Box display="flex" alignItems="center" justifyContent="center" style={{ gap: 8 }}>
                                <LinearProgress
                                  variant="determinate"
                                  value={p.percentage || 0}
                                  style={{ width: 60, height: 6, borderRadius: 3 }}
                                />
                                <Typography variant="caption" style={{ fontWeight: 600, minWidth: 32 }}>
                                  {p.percentage || 0}%
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell align="right" style={{ fontWeight: 600 }}>
                              {formatTime(p.totalSeconds)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </>
            )}

            {/* TAB 2: Daily Breakdown Table */}
            {activeTab === 'daily' && (
              <>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                  <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                    Day-by-Day Page Views Log ({data.dailyTrend.length} days)
                  </Typography>
                </Box>

                <TableContainer>
                  <Table className={classes.table} size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell className={classes.tableHeaderCell} width="20%">Date</TableCell>
                        <TableCell className={classes.tableHeaderCell} width="15%">Day</TableCell>
                        <TableCell align="right" className={classes.tableHeaderCell} width="20%">Daily Page Views</TableCell>
                        <TableCell align="center" className={classes.tableHeaderCell} width="15%">Active Users</TableCell>
                        <TableCell className={classes.tableHeaderCell} width="30%">Top Screen of the Day</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.dailyTrend.slice().reverse().map((d) => (
                        <TableRow key={d.dateKey} className={classes.tableRow}>
                          <TableCell style={{ fontWeight: 600 }}>{d.label}</TableCell>
                          <TableCell color="textSecondary">{d.dayName}</TableCell>
                          <TableCell align="right">
                            <Chip
                              size="small"
                              label={`${d.views} views`}
                              style={{
                                fontWeight: 700,
                                background: d.views > 0 ? 'rgba(16,185,129,0.15)' : 'rgba(148,163,184,0.1)',
                                color: d.views > 0 ? '#10b981' : '#94a3b8',
                              }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2" style={{ fontWeight: 600 }}>
                              {d.uniqueUsersCount > 0 ? d.uniqueUsersCount : '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {d.topPageName !== 'None' ? (
                              <Chip size="small" variant="outlined" label={d.topPageName} style={{ fontSize: '0.75rem' }} />
                            ) : (
                              <Typography variant="caption" color="textSecondary">—</Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
